/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DeviceType, useDevice, useUniqueId } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { DXP_ACTIVE_CLASS } from '../../Constants/Design';
import { ICON_CHEVRON_LEFT } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { AccordionProps, accordionDefaults } from './Accordion.props';

/**
 * SDS Accordion component.
 */
export const Accordion = (props: AccordionProps): JSX.Element => {
  const { body, classes, header, toggleTextHide, toggleTextShow, defaultOpen, ...rest } = {
    ...accordionDefaults,
    ...props,
  };

  const [activeClass, setActiveClass] = useState(defaultOpen ? DXP_ACTIVE_CLASS : ``);

  // Toggle content callback
  const toggleContent = useCallback((): void => {
    const newActiveClass = activeClass ? `` : DXP_ACTIVE_CLASS;
    setActiveClass(newActiveClass);
  }, [activeClass, setActiveClass]);

  const toggleText = activeClass ? toggleTextHide : toggleTextShow;
  const accordionClass = clsx(`sds-accordion-container`, activeClass, classes, {
    [`sds-accordion-container--basic`]: !body,
  });

  // Device detection
  const deviceType = useDevice();
  const isMobile = deviceType === DeviceType.mobile;

  // Toggle/Content IDs
  const uniqueId = useUniqueId();
  const toggleId = `sds-accordion-toggle--${uniqueId}`;
  const contentId = `sds-accordion-content--${uniqueId}`;

  return (
    <div className={accordionClass} {...rest}>
      {body ? (
        <>
          {isMobile ? (
            <>
              <div className="sds-accordion-header">{header}</div>
              <div aria-labelledby={toggleId} className="sds-accordion-content" id={contentId}>
                {body}
              </div>
              <div className="sds-accordion-footer">
                <button
                  aria-controls={contentId}
                  aria-expanded={!!activeClass}
                  className="sds-accordion-toggle"
                  id={toggleId}
                  onClick={toggleContent}
                >
                  <span>{toggleText}</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                aria-controls={contentId}
                aria-expanded={!!activeClass}
                className="sds-accordion-header"
                id={toggleId}
                onClick={toggleContent}
              >
                <div className="sds-accordion-header-content">
                  {header}
                  <div className="sds-accordion-icon">
                    <span className="sr-only">{toggleText}</span>
                    <SdsIcon svg={ICON_CHEVRON_LEFT} />
                  </div>
                </div>
              </button>
              <div aria-labelledby={toggleId} className="sds-accordion-content" id={contentId}>
                {body}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="sds-accordion-header">{header}</div>
      )}
    </div>
  );
};
